/* import __COLOCATED_TEMPLATE__ from './sidebar.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type ApplicationInstance from '@ember/application/instance';
import { tracked } from '@glimmer/tracking';
import {
  deleteRequest,
  postRequest,
  putRequest,
  type ResponseError,
} from 'embercom/lib/inbox/requests';
import { get } from 'embercom/lib/ajax';
import type NotificationsService from 'embercom/services/notifications-service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { dropTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type { Brand, BrandWireFormat } from '../brands/types/brand';
import type { HelpCenter } from '../brands/types/help-center';
import type { SenderEmailAddressSettings } from '../brands/types/sender-email-address-settings';
import { ref } from 'ember-ref-bucket';
import { getDomainName } from 'embercom/lib/email';
import { modifier } from 'ember-modifier';
import type ClipboardService from 'embercom/services/clipboard-service';
import type Router from '@ember/routing/router-service';
import { type Audience } from '../brands-new';
import type { AiAgentIdentity } from 'embercom/components/ai-agent/identity/ai-agent-identities-editor';
import type CallingPhoneNumber from 'embercom/models/calling-phone-number';
import type SmsPhoneNumber from 'embercom/models/sms/phone-number';

class PhoneFilterState {
  @tracked showCalling = true;
  @tracked showSms = true;

  get isAll() {
    return this.showCalling && this.showSms;
  }

  selectAll = () => {
    this.showCalling = true;
    this.showSms = true;
  };

  toggleCalling = () => {
    this.showCalling = !this.showCalling;
  };

  toggleSms = () => {
    this.showSms = !this.showSms;
  };
}

interface Args {
  type: 'new' | 'edit';
  isOpen: boolean;
  onClose: (brand: Brand | undefined) => void;
  helpCenters: HelpCenter[];
  onNewHelpCenter: (helpCenter: HelpCenter) => void;
  senderEmailAddressSettings?: SenderEmailAddressSettings[]; // only applicable for edit-brand sidebar
  onNewSenderEmailAddressSettings: (senderEmailAddressSettings: SenderEmailAddressSettings) => void;
  brand?: Brand; // only applicable for edit-brand sidebar
  domains: { name: string; isAuthenticated: boolean }[];
  onBrandDeletion?: () => void;
  aiAgentIdentity?: AiAgentIdentity;
  audiences?: Audience[];
  callingPhoneNumbers?: CallingPhoneNumber[];
  smsPhoneNumbers?: SmsPhoneNumber[];
  allBrands?: Brand[];
}

interface Signature {
  Args: Args;
  Element: never;
}

interface BrandRequestParams {
  app_id: string;
  help_center_id?: number;
  sender_email_address_settings_id?: number;
  fin_email_address_id?: number;
  notification_email_address?: string;
  name?: string;
  calling_phone_number_ids?: number[];
  brand_phone_numbers?: string[];
}

type DependentMessengerStyle = {
  id: string;
  name: string;
  platform: string;
};

type DependentEmailAddress = {
  id: string;
  email: string;
};

type DependentCallingPhoneNumber = {
  id: number;
  name: string;
  phone_number: string;
};

type DependentPhoneNumber = {
  phone_number: string;
  name: string;
  channel_type: string;
};

export type DependencyData = {
  brand_id: string;
  dependent_messenger_styles?: DependentMessengerStyle[];
  dependent_email_addresses?: DependentEmailAddress[];
  dependent_calling_phone_numbers?: DependentCallingPhoneNumber[];
  dependent_phone_numbers?: DependentPhoneNumber[];
};

export default class Sidebar extends Component<Signature> {
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare clipboardService: ClipboardService;
  @service declare router: Router;

  @tracked name?: string;
  @tracked filename?: string;
  @tracked fileData?: string;
  @tracked helpCenterId?: number;
  @tracked senderEmailAddressSettingsId?: number;
  @tracked finEmailAddressId?: number;

  // TODO: Remove once mbf-sms-brand-phone-number-picker is GA — selectedPhoneNumbers replaces this
  @tracked callingPhoneNumberIds: number[] = [];
  @tracked selectedPhoneNumbers: string[] = [];
  filterState = new PhoneFilterState();

  @tracked isNewAddressModalOpen = false;
  @tracked isNewHelpCenterModalOpen = false;

  @tracked showDependencyWarningModal = false;
  @tracked dependencyData: DependencyData;

  @ref('logoInput') declare logoInput: HTMLInputElement;

  clipboard?: any;

  copyBrandIdContainer = modifier(
    () => {
      this.onRender();
    },
    { eager: false },
  );

  constructor(owner: ApplicationInstance, args: Args) {
    super(owner, args);
    // The `&& this.args.brand` here is just to make typescript happy. If this
    // is an edit sidebar, then `this.args.brand` is always passed.
    if (this.isEditSidebar && this.args.brand) {
      this.name = this.args.brand.name;
      this.helpCenterId = this.args.brand.helpCenterId || undefined;
      this.senderEmailAddressSettingsId = this.args.brand.senderEmailAddressSettingsId || undefined;

      taskFor(this.loadFinEmailAddressForBrand).perform();
    }
    this.callingPhoneNumberIds = this.args.brand?.callingPhoneNumberIds ?? [];
    this.selectedPhoneNumbers = this.args.brand?.brandPhoneNumbers ?? [];
    this.dependencyData = { brand_id: this.args.brand?.id?.toString() || '' };
  }

  get isNewSidebar() {
    return this.args.type === 'new';
  }

  get isEditSidebar() {
    return this.args.type === 'edit';
  }
  get shouldShowDeleteButton() {
    return this.args.brand && !this.args.brand.isDefault;
  }

  @action
  handleClose(brand: Brand | undefined, isBrandChanged: boolean) {
    this.args.onClose(brand);
    if (!isBrandChanged) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: `cancel_updating_brand`,
        place: 'brands',
        metadata: {
          brand_id: this.args.brand?.id,
          is_default_brand: this.args.brand?.isDefault,
          brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
        },
      });
    }
  }

  get helpCenterItems() {
    return this.args.helpCenters.map((helpCenter) => ({
      value: helpCenter.id,
      text: helpCenter.name,
      website_turned_on: helpCenter.website_turned_on,
    }));
  }

  @action
  onSelectHelpCenter(helpCenterId: number) {
    this.helpCenterId = helpCenterId;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'selected',
      object: `help_center`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        help_center_id: helpCenterId,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
        is_help_center_live: this.helpCenterItems.find((item) => item.value === helpCenterId)
          ?.website_turned_on,
      },
    });
  }

  @action
  handleClickNewHelpCenter() {
    this.isNewHelpCenterModalOpen = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `new_help_center_button`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      },
    });
  }

  @action
  handleCloseNewHelpCenterModal() {
    this.isNewHelpCenterModalOpen = false;
  }

  @action
  handleCreateNewHelpCenter(helpCenter: HelpCenter) {
    this.args.onNewHelpCenter(helpCenter);
    this.helpCenterId = helpCenter.id;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'created',
      object: `help_center`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        help_center_id: helpCenter.id,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
        is_help_center_live: false,
      },
    });
  }

  isEmailDomainAuthenticated(email: string) {
    let emailDomain = getDomainName(email);
    return this.args.domains.find((domain) => domain.name === emailDomain)?.isAuthenticated;
  }

  isItemDisabled(senderEmailAddressSetting: SenderEmailAddressSettings) {
    return (
      !this.isEmailDomainAuthenticated(senderEmailAddressSetting.email) ||
      !senderEmailAddressSetting.verified
    );
  }

  get senderEmailAddressSettingsItems() {
    let senderEmailAddressSettings = this.args.senderEmailAddressSettings;

    return senderEmailAddressSettings && senderEmailAddressSettings?.length
      ? senderEmailAddressSettings.map((senderEmailAddressSetting) => ({
          value: senderEmailAddressSetting.id,
          text: senderEmailAddressSetting.email,
          isDisabled: this.isItemDisabled(senderEmailAddressSetting),
          component: 'new-settings/workspace/brands/brand-email-dropdown-item',
          componentAttrs: {
            verified: senderEmailAddressSetting.verified,
            isSelected: this.senderEmailAddressSettingsId === senderEmailAddressSetting.id,
            isAuthenticated: this.isEmailDomainAuthenticated(senderEmailAddressSetting.email),
          },
        }))
      : [
          {
            value: 0,
            text: '',
            component: 'new-settings/workspace/brands/brand-email-dropdown-empty-list',
            isDisabled: true,
          },
        ];
  }

  @action
  onSelectSenderEmailAddressSetting(senderEmailAddressSettingsId: number) {
    this.senderEmailAddressSettingsId = senderEmailAddressSettingsId;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'selected',
      object: `default_address`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        sender_email_address_settings_id: this.senderEmailAddressSettingsId,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      },
    });
  }

  @action
  handleClickNewAddress() {
    this.isNewAddressModalOpen = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `new_address_button`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        sender_email_address_settings_id: this.senderEmailAddressSettingsId,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      },
    });
  }

  @action
  handleCloseNewAddressModal() {
    this.isNewAddressModalOpen = false;
  }

  @action
  handleCloseDependencyWarningModal() {
    this.showDependencyWarningModal = false;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `cancel_delete_brand_button`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      },
    });
  }

  @action
  handleCreateNewAddress(senderEmailAddressSettings: SenderEmailAddressSettings) {
    this.args.onNewSenderEmailAddressSettings(senderEmailAddressSettings);
    this.senderEmailAddressSettingsId = senderEmailAddressSettings.id;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'created',
      object: `default_address`,
      place: 'brands',
      brand_id: this.args.brand?.id,
      metadata: {
        sender_email_address_settings_id: this.senderEmailAddressSettingsId,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      },
    });
  }

  @action
  handleClickMessengerStyles() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `messenger_styles_button`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      },
    });
    safeWindowOpen(
      '/a/apps/_/settings/channels/messenger/web?section=general&tab=styling',
      '_blank',
    );
  }

  get canUsePhoneNumberAssignment() {
    return this.appService.app.canUseFeature('mbf-calling-phone-number-brand-assignment');
  }

  get canUseSmsPhoneNumberPicker() {
    return this.appService.app.canUseFeature('mbf-sms-brand-phone-number-picker');
  }

  // TODO: Remove once mbf-sms-brand-phone-number-picker is GA — phoneNumbersAssignedToOtherBrands replaces this
  get phoneNumberIdsAssignedToOtherBrands(): Set<number> {
    let assignedIds = new Set<number>();
    let currentBrandId = this.args.brand?.id;
    for (let brand of this.args.allBrands ?? []) {
      if (brand.id !== currentBrandId && brand.callingPhoneNumberIds) {
        for (let id of brand.callingPhoneNumberIds) {
          assignedIds.add(id);
        }
      }
    }
    return assignedIds;
  }

  get phoneNumbersAssignedToOtherBrands(): Set<string> {
    let assigned = new Set<string>();
    let currentBrandId = this.args.brand?.id;
    for (let brand of this.args.allBrands ?? []) {
      if (brand.id !== currentBrandId && brand.brandPhoneNumbers) {
        for (let pn of brand.brandPhoneNumbers) {
          assigned.add(pn);
        }
      }
    }
    return assigned;
  }

  get phoneNumberItems() {
    let assignedToOthers = this.phoneNumbersAssignedToOtherBrands;
    let callingTooltip = this.intl.t('new-settings.workspace.brands.phone-numbers.tooltip-calling');
    let smsTooltip = this.intl.t('new-settings.workspace.brands.phone-numbers.tooltip-sms');

    // Build lookup of calling numbers by E.164
    let callingPhoneNumbers = this.args.callingPhoneNumbers ?? [];
    let callingByE164: Record<string, { name: string; formattedNumber: string }> = {};
    callingPhoneNumbers
      .filter((pn) => pn && pn.isActive)
      .forEach((pn) => {
        let e164 = pn.phoneNumber;
        callingByE164[e164] = {
          name: pn.name || '',
          formattedNumber: pn.formattedPhoneNumber || pn.phoneNumber,
        };
      });

    // Build lookup of SMS numbers by E.164
    let smsByE164: Record<string, { name: string; formattedNumber: string }> = {};
    if (this.canUseSmsPhoneNumberPicker) {
      let smsPhoneNumbers = this.args.smsPhoneNumbers ?? [];
      smsPhoneNumbers
        .filter((pn) => pn)
        .forEach((pn) => {
          let e164 = pn.number;
          smsByE164[e164] = {
            name: pn.name || '',
            formattedNumber: e164,
          };
        });
    }

    // Merge into unified items with custom component
    let allE164s = new Set([...Object.keys(callingByE164), ...Object.keys(smsByE164)]);
    let items: any[] = [];
    if (this.canUseSmsPhoneNumberPicker) {
      items.push({
        value: '__filter_header__',
        text: '',
        isDisabled: true,
        component: 'new-settings/workspace/brands/brand-phone-filter-header',
        componentShouldReplaceItem: true,
        componentAttrs: {
          filterState: this.filterState,
          callingLabel: this.intl.t('new-settings.workspace.brands.phone-numbers.channel-calling'),
          smsLabel: this.intl.t('new-settings.workspace.brands.phone-numbers.channel-sms'),
        },
      });
    }

    allE164s.forEach((e164) => {
      if (assignedToOthers.has(e164)) {
        return;
      }
      let isCalling = e164 in callingByE164;
      let isSms = e164 in smsByE164;
      let record = isCalling ? callingByE164[e164] : smsByE164[e164];

      items.push({
        value: e164,
        text: record.name || record.formattedNumber,
        isSelected: this.selectedPhoneNumbers.includes(e164),
        component: 'new-settings/workspace/brands/brand-phone-dropdown-item',
        componentShouldReplaceItem: true,
        componentAttrs: {
          formattedNumber: record.formattedNumber,
          hasName: Boolean(record.name),
          isCalling,
          isSms,
          callingTooltip,
          smsTooltip,
          filterState: this.filterState,
        },
      });
    });

    return items;
  }

  get phoneNumberOpenerText(): string | undefined {
    if (this.selectedPhoneNumbers.length === 0) {
      return undefined;
    }
    let firstE164 = this.selectedPhoneNumbers[0];
    let callingMatch = (this.args.callingPhoneNumbers ?? []).find(
      (pn) => pn.phoneNumber === firstE164,
    );
    let smsMatch = !callingMatch
      ? (this.args.smsPhoneNumbers ?? []).find((pn) => pn.number === firstE164)
      : null;
    let match = callingMatch || smsMatch;
    let firstName = match
      ? match.name || (callingMatch?.formattedPhoneNumber ?? firstE164)
      : firstE164;
    let remaining = this.selectedPhoneNumbers.length - 1;
    return remaining > 0 ? `${firstName} (+${remaining})` : firstName;
  }

  @action
  toggleCallingFilter() {
    this.filterState.toggleCalling();
  }

  @action
  toggleSmsFilter() {
    this.filterState.toggleSms();
  }

  @action
  onSelectPhoneNumber(phoneNumber: string) {
    if (phoneNumber === '__filter_header__') {
      return;
    }
    if (this.selectedPhoneNumbers.includes(phoneNumber)) {
      this.selectedPhoneNumbers = this.selectedPhoneNumbers.filter((pn) => pn !== phoneNumber);
    } else {
      this.selectedPhoneNumbers = [...this.selectedPhoneNumbers, phoneNumber];
    }
  }

  @action
  handleClickNewPhoneNumber() {
    let url: string = this.router.urlFor('apps.app.settings.calling');
    safeWindowOpen(url);
  }

  get isReady() {
    return this.name && this.helpCenterId !== undefined;
  }

  get isNameTooLong() {
    return this.name && this.name.length > 255;
  }

  get isNameMissing() {
    return !this.name || !this.name.trim();
  }

  @dropTask
  *saveTask(): TaskGenerator<void> {
    if (this.isNameTooLong) {
      return;
    }

    let wireFormat: BrandWireFormat;
    try {
      let params: BrandRequestParams = {
        app_id: this.appService.app.id,
        help_center_id: this.helpCenterId,
        sender_email_address_settings_id: this.senderEmailAddressSettingsId,
        fin_email_address_id: this.finEmailAddressId,
      };
      if (!this.args.brand?.isDefault) {
        params['name'] = this.name;
      }
      if (!this.isNewSidebar && this.canUsePhoneNumberAssignment && !this.args.brand?.isDefault) {
        params['brand_phone_numbers'] = this.selectedPhoneNumbers;
      }
      let response;
      let object;
      if (this.isNewSidebar) {
        response = yield postRequest(`/ember/brands`, params);
        object = 'create_brand_button';
      } else {
        response = yield putRequest(`/ember/brands/${this.args.brand?.id}`, params);
        object = 'update_brand_button';
      }
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        place: 'brands',
        object,
        app_id: this.appService.app.id,
        help_center_id: this.helpCenterId,
        sender_email_address_settings_id: this.senderEmailAddressSettingsId,
        brand_id: object === 'update_brand_button' ? this.args.brand?.id : undefined,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      });
      wireFormat = yield response.json() as BrandWireFormat;
      if (
        this.isNewSidebar &&
        this.canUsePhoneNumberAssignment &&
        this.selectedPhoneNumbers.length > 0
      ) {
        let updateResponse = yield putRequest(`/ember/brands/${wireFormat.id}`, {
          app_id: this.appService.app.id,
          brand_phone_numbers: this.selectedPhoneNumbers,
        });
        wireFormat = yield updateResponse.json() as BrandWireFormat;
      }
      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.workspace.brands.brand-saved-successfully'),
      );
    } catch (error) {
      console.error('saveTask() error', error);
      let message =
        (yield this.getErrorMessage(error)) ||
        this.intl.t('new-settings.workspace.brands.failed-to-save-brand');
      this.notificationsService.notifyResponseError(error, {
        default: message,
      });
      return;
    }
    let brand = {
      id: wireFormat.id,
      name: wireFormat.name,
      logoId: wireFormat.logo_id,
      logoUrl: wireFormat.logo_url,
      helpCenterId: wireFormat.help_center_id,
      senderEmailAddressSettingsId: wireFormat.sender_email_address_settings_id,
      isDefault: wireFormat.is_default,
      callingPhoneNumberIds: wireFormat.calling_phone_number_ids,
      brandPhoneNumbers: wireFormat.brand_phone_numbers,
    } as Brand;
    this.handleClose(brand, true);
  }

  private async getErrorMessage(error: ResponseError) {
    // If `error` doesn't conform to this shape, just fall back to a default
    // error message (as determined by the callsite).
    try {
      let response: any = await error.response.json();
      if (response?.errors?.length) {
        return response.errors[0].message;
      }
    } catch {
      return null;
    }
  }

  @action
  handleSave() {
    try {
      taskFor(this.saveTask).perform();
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('new-settings.workspace.brands.failed-to-save-brand'),
      });
    }
  }

  @action
  handleLearnMoreClick() {
    window.Intercom('showArticle', 9744851);
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `brands_learn_more_button`,
      place: 'brands',
      metadata: {
        brand_id: this.args.brand?.id,
        is_default_brand: this.args.brand?.isDefault,
        brand_type: this.args.brand?.isDefault ? 'default' : 'non-default',
      },
    });
  }

  get isSaving() {
    return taskFor(this.saveTask).isRunning;
  }

  get saveDisabled(): boolean {
    let canSave = this.isReady && !this.isSaving && !this.isNameTooLong && !this.isNameMissing;
    return !canSave;
  }

  get saveTooltipText(): string {
    if (this.isSaving) {
      return this.intl.t('new-settings.workspace.brands.saving');
    }

    if (this.saveDisabled) {
      if (this.isNameTooLong) {
        return this.intl.t('new-settings.workspace.brands.brand-name-too-long');
      }

      if (this.isNameMissing) {
        return this.intl.t('new-settings.workspace.brands.brand-name-missing-error');
      }

      if (!this.helpCenterId) {
        return this.intl.t('new-settings.workspace.brands.help-center-missing-error');
      }
    }

    return this.intl.t('new-settings.workspace.brands.save');
  }

  get brand(): Brand {
    return this.args.brand!;
  }

  @action
  aiAgentAvatarUrl(aiAgentIdentity: AiAgentIdentity | undefined): string {
    return aiAgentIdentity?.avatar?.imageUrls?.square128 || '';
  }

  @action
  aiAgentIdentityName(aiAgentIdentity: AiAgentIdentity | undefined): string {
    return aiAgentIdentity?.name || '';
  }

  @action
  handleDependentObjectCheck() {
    try {
      taskFor(this.fetchDependentObjects).perform();
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: `delete_brand_button`,
        place: 'brands',
        metadata: {
          brand_id: this.args.brand?.id,
          is_default_brand: this.args.brand?.isDefault,
          brand_type: 'non-default',
          style_count: this.dependencyData.dependent_messenger_styles?.length,
          address_count: this.dependencyData.dependent_email_addresses?.length,
        },
      });
    } catch (error) {
      console.error('Failed to check brand dependencies:', error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('new-settings.workspace.brands.failed-to-fetch-dependent-objects'),
      });
    }
  }

  get isFetchingDependentObjects() {
    return taskFor(this.fetchDependentObjects).isRunning;
  }

  @dropTask
  *fetchDependentObjects(): TaskGenerator<void> {
    try {
      this.showDependencyWarningModal = true;
      this.dependencyData = yield get(
        `/ember/brands/${this.args.brand?.id}/dependent_objects?app_id=${this.appService.app.id}`,
      ) as DependencyData;
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('new-settings.workspace.brands.failed-to-fetch-dependent-objects'),
      });
      this.showDependencyWarningModal = false;
    }
  }

  @action
  handleDelete() {
    try {
      taskFor(this.deleteBrand).perform();
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: `confirm_delete_brand_button`,
        place: 'brands',
        metadata: {
          brand_id: this.args.brand?.id,
          is_default_brand: this.args.brand?.isDefault,
          brand_type: 'non-default',
          style_count: this.dependencyData.dependent_messenger_styles?.length,
          address_count: this.dependencyData.dependent_email_addresses?.length,
        },
      });
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('new-settings.workspace.brands.failed-to-delete-brand'),
      });
    }
  }

  get isDeleting() {
    return taskFor(this.deleteBrand).isRunning;
  }

  @action
  redirectTo(redirectRoute: string, queryParams?: Record<string, any>) {
    this.router.transitionTo(redirectRoute, { queryParams });
  }

  @dropTask
  *deleteBrand(): TaskGenerator<void> {
    try {
      yield deleteRequest(`/ember/brands/${this.args.brand?.id}?app_id=${this.appService.app.id}`);
      this.showDependencyWarningModal = false;
      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.workspace.brands.brand-deleted-successfully'),
      );
      this.args.onBrandDeletion?.();
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('new-settings.workspace.brands.failed-to-delete-brand'),
      });
    }
  }

  onRender() {
    let copyBrandIdButton = document.querySelector('#brand-id-input-wrapper .form__input-suffix');
    if (copyBrandIdButton) {
      copyBrandIdButton.classList.add('cursor-pointer');
      if (!this.clipboard) {
        this.clipboard = this.clipboardService.createClipboard(
          copyBrandIdButton,
          this.args.brand?.id.toString() ?? '',
          () => {
            this.notificationsService.notifyConfirmation(
              this.intl.t('new-settings.workspace.brands.brand-id.copy-success'),
            );
          },
          () => {
            this.notificationsService.notifyWarning(
              this.intl.t('new-settings.workspace.brands.brand-id.copy-error'),
            );
          },
        );
      }
    }
  }

  @action
  onSelectFinEmailAddress(finEmailAddressId: number) {
    this.finEmailAddressId = finEmailAddressId;
  }

  @dropTask
  *loadFinEmailAddressForBrand(): TaskGenerator<void> {
    if (!this.args.brand?.id) {
      return;
    }

    try {
      let url = this.args.brand.isDefault
        ? `/ember/fin_email_settings?app_id=${this.appService.app.id}`
        : `/ember/fin_email_settings?brand_id=${this.args.brand.id}&app_id=${this.appService.app.id}`;
      let response = yield get(url);
      this.finEmailAddressId = response?.id;
    } catch (error) {
      let message =
        (yield this.getErrorMessage(error)) ||
        this.intl.t('new-settings.workspace.brands.failed-to-load-identity-emails');
      this.notificationsService.notifyResponseError(error, {
        default: message,
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::BrandsNew::Sidebar': typeof Sidebar;
  }
}
